import EditHeader from "common/components/EditHeader/EditHeader";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useRouter from "common/hooks/use-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import usePark from "common/hooks/usePark";
import AnimalSelectSection from "components/select/AnimalSelect/AnimalSelectSection";
import { Animal } from "types/Animal";
import { Specy } from "types/Specy";
// import { ObservationGrid } from "types/ObservationGrid";
import TagSelect from "components/select/TagSelect/TagSelect";
import { Tag } from "types/Tag";
// import GenusSelect from "components/select/GenusSelect/GenusSelect";
import ObservationGridSelect from "components/select/ObservationGridSelect/ObservationGridSelect";
import useAuth from "common/hooks/useAuth";
import EnclosureSelect from "components/select/EnclosureSelect/EnclosureSelect";

type Props = {
  onSubmit: (value: any) => any;
  specy?: Specy;
  onCancel?: () => void;
  additionalHeaderStyle?: any;
};

function SpecyForm({
  onSubmit,
  specy,
  onCancel,
  additionalHeaderStyle,
}: Props) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const { navigate } = useRouter();
  const { park: currentPark } = usePark();
  const { user: currentUser } = useAuth();
  const canManageSpecies = specy?.id
    ? (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.species?.update)) ??
      false
    : (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.species?.create)) ??
      false;

  const initialValues = useMemo(
    () =>
      specy?.id
        ? {
            ...specy,
            park: specy.park?.id ?? currentPark?.id,
            animals: specy.animals?.map((a: Animal) => a.id),
            // genus: specy.genus?.id,
            tags: specy?.tags?.map((tag: Tag) => tag.id),
            observationGrids: specy?.observationGrids?.map((grid) => grid.id),
          }
        : {
            name: "",
            scientifiName: "",
            englishName: "",
            park: currentPark?.id,
          },
    [specy, currentPark]
  );

  const additionalFilters = [
    {
      key: "enclosure",
      name: t("common.enclosures"),
      component: <EnclosureSelect />,
    },
    {
      key: "tags",
      name: t("forms.tags"),
      component: <TagSelect />,
    },
  ];

  const onSubmitWrapper = async (values: any) => {
    const newValues = { ...values };
    if (typeof values.observationGrids === "number") {
      newValues.observationGrids = [{ id: values.observationGrids }];
    }

    const res = await onSubmit(newValues);

    if (res?.message) {
      setErrorMessage(res?.message);
    }
  };

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/species");
    }
  }, [navigate, onCancel]);

  const [isContentSmall, setIsContentSmall] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkContentSize = () => {
      if (contentRef.current) {
        const contentWidth = contentRef.current.scrollWidth;
        setIsContentSmall(contentWidth < 768); // Ajustez la valeur selon vos besoins
      }
    };

    checkContentSize();
    window.addEventListener("resize", checkContentSize);

    return () => {
      window.removeEventListener("resize", checkContentSize);
    };
  }, []);

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      render={({ form, handleSubmit, values }) => {
        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(specy?.id ? "editItem.specy" : "newItem.specy")}
              subtitle={values.name}
              icon="BsFeather"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={canManageSpecies ? handleSubmit : undefined}
                  onCancel={handleCancel}
                  additionalStyle={additionalHeaderStyle}
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div
                ref={contentRef}
                className={`flex ${
                  isContentSmall ? "flex-col" : "flex-row"
                } flex-wrap gap-2 mt-2 w-full`}
              >
                <div
                  className={`white-box flex flex-col gap-2 mb-2 md:mb-0 ${
                    isContentSmall ? "w-full" : "flex-1"
                  }`}
                >
                  <div className="flex flex-col gap-2">
                    <FormControl
                      name="scientificName"
                      title={t("forms.scientificName")}
                      containerStyle=""
                      required
                      editable={canManageSpecies}
                    />
                    <FormControl
                      name="name"
                      title={t("forms.commonName")}
                      containerStyle=""
                      editable={canManageSpecies}
                    />
                    <FormControl
                      name="englishName"
                      title={t("forms.englishName")}
                      containerStyle=""
                      editable={canManageSpecies}
                    />
                    {/* <FormControl
                      title={t("forms.genus")}
                      type="custom"
                      name="genus"
                      editable={canManageSpecies}
                    >
                      <GenusSelect />
                    </FormControl> */}
                    <FormControl
                      name="geriatricAge"
                      title={t("forms.geriatricAge")}
                      editable={canManageSpecies}
                      type="number"
                    />
                    <FormControl
                      title={t("forms.observationGrids")}
                      type="custom"
                      isSelect
                      name="observationGrids"
                      editable={canManageSpecies}
                      multiple
                    >
                      <ObservationGridSelect
                      // multiple
                      />
                    </FormControl>
                    <FormControl
                      name="tags"
                      title={t("forms.tags")}
                      type="custom"
                      editable={canManageSpecies}
                    >
                      <TagSelect multiple />
                    </FormControl>
                    <FormControl
                      name="food"
                      title={t("forms.food")}
                      editable={canManageSpecies}
                      type="textarea"
                      className="w-full"
                    />
                  </div>
                </div>
                <div
                  className={`white-box flex flex-col gap-2 justify-between items-center ${
                    isContentSmall ? "w-full" : "flex-1"
                  } h-fit`}
                >
                  <div className="flex-1 w-full">
                    <FormControl
                      title={t("forms.animals")}
                      type="custom"
                      isSelect
                      name="animals"
                      editable={canManageSpecies}
                    >
                      <AnimalSelectSection
                        form={form}
                        values={values}
                        additionalFilters={additionalFilters}
                      />
                    </FormControl>
                  </div>
                </div>
              </div>

              <HeaderButtonsEdit
                onSubmit={handleSubmit}
                onCancel={() => navigate("/species")}
              />
            </form>
          </div>
        );
      }}
    />
  );
}

export default SpecyForm;
